import { type ReactElement, type ReactNode, useMemo } from 'react'
import { useAccountStatusesMap } from '@front/model'
import { type ComboboxItem, type ComboboxLikeRenderOptionInput, Group, Select, type SelectProps } from '@mantine/core'
import { type AccountStatusId, isAccountStatusId, isNotDefined } from '@terros/common'
import { MaterialSymbolIcon } from '../terros'
import { AccountStatusInfo } from './AccountStatusInfo'
import { useAccountStageSelectItems } from './useAvailableStatuses'

type ValueProps =
  | {
      statusId: AccountStatusId
      setStatusId: (statusId: AccountStatusId) => void
      allowDeselect?: false
    }
  | {
      statusId: AccountStatusId | undefined
      setStatusId: (statusId: AccountStatusId | undefined) => void
      allowDeselect: true
    }

type Props = ValueProps &
  Omit<SelectProps, 'data' | 'onChange' | 'value' | 'allowDeselect'> & {
    availableStatuses?: AccountStatusId[]
    allowArchived?: boolean
    isDisabled?: (statusId: AccountStatusId) => boolean
  }

export const AccountStatusSelect = ({
  statusId,
  setStatusId,
  allowDeselect,
  availableStatuses,
  allowArchived,
  isDisabled,
  ...props
}: Props): ReactElement => {
  const stagesMap = useAccountStatusesMap({ archived: 'all' })
  const { data, isLoading } = useAccountStageSelectItems({ availableStatuses, allowArchived, isDisabled })
  const currentValue = useMemo(() => {
    if (isNotDefined(statusId)) return null
    return statusId
  }, [statusId])

  const renderOption = ({ option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>): ReactNode => {
    if (!isAccountStatusId(option.value)) return
    const status = stagesMap.get(option.value)
    if (!status) return

    return (
      <Group justify='space-between' className='flex-1'>
        <AccountStatusInfo statusId={status.statusId} size='sm' />
        {checked && <MaterialSymbolIcon icon='check' />}
      </Group>
    )
  }
  return (
    <Select
      placeholder={isNotDefined(statusId) ? 'Account Stage' : undefined}
      renderOption={renderOption}
      {...props}
      allowDeselect={allowDeselect}
      clearable={allowDeselect}
      data={data}
      disabled={isLoading ?? props.disabled}
      miw={250}
      value={currentValue}
      onChange={(s) => {
        if (allowDeselect && s === null) {
          setStatusId(undefined)
        } else if (isAccountStatusId(s)) setStatusId(s)
      }}
    />
  )
}
